import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Bug, Lightbulb, Activity, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useSite } from "../context/SiteContext";
import { Reveal } from "../components/motion";

const TYPES = [
  { key: "bug", label: "Report a bug", icon: Bug },
  { key: "feature", label: "Suggest a feature", icon: Lightbulb },
  { key: "question", label: "Ask a question", icon: MessageCircle },
  { key: "report", label: "Report abuse", icon: Mail },
];

export default function Support() {
  const { site } = useSite();
  const [form, setForm] = useState({ name: "", email: "", type: "bug", subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/public/tickets", form);
      toast.success(res.data.message);
      setForm({ name: "", email: "", type: form.type, subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit ticket");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="pt-16 min-h-screen" data-testid="support-page">
      <section className="hero-glow border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.35em] text-volt font-semibold mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-volt inline-block" /> We've got you
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight" style={{ fontWeight: 900 }} data-testid="support-headline">
              SUPPORT <span className="text-outline-blurple">SQUAD</span>
            </h1>
            <p className="text-muted-foreground text-base md:text-lg mt-6 max-w-xl">
              Real humans, fast answers. Pick a channel below — the Discord server is usually the fastest.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2 space-y-4">
          <Reveal>
            <a
              href={site?.support_invite_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="support-join-discord"
              className="card-beam rounded-xl border border-blurple/40 bg-blurple/10 p-7 flex items-center gap-5 hover:-translate-y-1 transition-transform duration-300 block"
            >
              <div className="w-12 h-12 rounded-lg bg-blurple flex items-center justify-center shrink-0">
                <MessageCircle size={22} className="text-white" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-white">Join the Discord</h3>
                <p className="text-sm text-muted-foreground mt-1">Live help from staff and 12k+ community members.</p>
              </div>
            </a>
          </Reveal>
          <Reveal delay={0.08}>
            <Link
              to="/status"
              data-testid="support-status-link"
              className="rounded-xl border border-white/10 bg-white/[0.03] p-7 flex items-center gap-5 hover:-translate-y-1 hover:border-emerald-400/40 transition-[transform,border-color] duration-300 block"
            >
              <div className="w-12 h-12 rounded-lg bg-emerald-400/15 border border-emerald-400/30 flex items-center justify-center shrink-0">
                <Activity size={22} className="text-emerald-400" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-white">Check system status</h3>
                <p className="text-sm text-muted-foreground mt-1">Something feels off? See if it's us before reporting.</p>
              </div>
            </Link>
          </Reveal>
          <Reveal delay={0.16}>
            <Link
              to="/docs/troubleshooting"
              data-testid="support-troubleshooting-link"
              className="rounded-xl border border-white/10 bg-white/[0.03] p-7 flex items-center gap-5 hover:-translate-y-1 hover:border-volt/40 transition-[transform,border-color] duration-300 block"
            >
              <div className="w-12 h-12 rounded-lg bg-volt/15 border border-volt/30 flex items-center justify-center shrink-0">
                <Bug size={22} className="text-volt" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-white">Troubleshooting guide</h3>
                <p className="text-sm text-muted-foreground mt-1">No sound? Bot won't join? Start here.</p>
              </div>
            </Link>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-3">
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-8" data-testid="ticket-form-card">
            <h2 className="font-heading text-2xl font-bold text-white mb-2">Open a ticket</h2>
            <p className="text-sm text-muted-foreground mb-7">Staff replies within a day — usually much faster.</p>
            <div className="flex flex-wrap gap-2 mb-6" data-testid="ticket-type-picker">
              {TYPES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setForm({ ...form, type: t.key })}
                  data-testid={`ticket-type-${t.key}`}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${
                    form.type === t.key ? "bg-blurple text-white" : "border border-white/15 text-white/60 hover:text-white"
                  }`}
                >
                  <t.icon size={14} /> {t.label}
                </button>
              ))}
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  value={form.name}
                  onChange={set("name")}
                  required
                  minLength={2}
                  placeholder="Discord username"
                  data-testid="ticket-name-input"
                  className="bg-white/5 border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-blurple"
                />
                <input
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  required
                  placeholder="Email"
                  data-testid="ticket-email-input"
                  className="bg-white/5 border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-blurple"
                />
              </div>
              <input
                value={form.subject}
                onChange={set("subject")}
                required
                minLength={4}
                placeholder="Subject"
                data-testid="ticket-subject-input"
                className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-blurple"
              />
              <textarea
                value={form.message}
                onChange={set("message")}
                required
                minLength={10}
                rows={5}
                placeholder="Tell us what's happening — include your server name if it's about a specific server."
                data-testid="ticket-message-input"
                className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-blurple resize-none"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="ticket-submit-button"
                className="bg-blurple hover:bg-[#4752c4] disabled:opacity-50 text-white font-heading font-bold px-8 py-3.5 rounded-full transition-colors duration-200"
              >
                {busy ? "Sending…" : "Send ticket"}
              </button>
            </form>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
